/**
 * "On this device" (models.md §12 item 2, §6.3–6.4; the prototype's install block `installMarkup`): the
 * download state of an on-device model with its progress and bytes, and Download · Pause · Resume · Cancel
 * · Retry · Remove from this device. Every state is said in words — verifying, failed (with why), evicted by
 * the browser, storage that is not persistent, a browser that cannot keep the files at all, no runtime.
 * Nothing starts by itself: a download only ever starts from a click here, in the banner or onboarding.
 */

import type { EmbeddingCatalogEntry, ModelInstallState } from '@digester/core';
import { openDialog } from '../../app/dialogs.js';
import { reportError } from '../../app/errors.js';
import { showToast } from '../../app/Toast.js';
import { useEngine, useEngineStatus } from '../../engine-client/EngineProvider.js';
import { Icon } from '../../icons/Icon.js';
import { cannotStart, installErrorText, megabytes, percent } from './catalog.js';

type Action = 'download' | 'pause' | 'cancel' | 'remove';

/** `navigator.connection.saveData` and `navigator.deviceMemory`, where the browser has them. */
function deviceHints(): { saveData: boolean; memoryGb: number | null } {
  const nav = (typeof navigator === 'undefined' ? {} : navigator) as { connection?: { saveData?: boolean }; deviceMemory?: number };
  return { saveData: nav.connection?.saveData === true, memoryGb: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null };
}

/** Start (or resume) the download of `entry`; with Save-Data on, the size is confirmed first (models.md §6.3). */
export function useModelDownload(): (entry: EmbeddingCatalogEntry) => void {
  const engine = useEngine();
  return (entry) => {
    const start = async (): Promise<void> => {
      try {
        await engine.models.download(entry.id);
      } catch (error) {
        reportError(error);
      }
    };
    if (deviceHints().saveData) {
      openDialog('confirm', {
        title: `Download ${entry.name}?`,
        message: `This downloads ${megabytes(entry.downloadBytes)} to this device. Your browser asks to save data.`,
        confirmLabel: `Download ${megabytes(entry.downloadBytes)}`,
        onConfirm: start,
      });
    } else void start();
  };
}

export function InstallBlock({ entry, state, unavailable, enabled }: { entry: EmbeddingCatalogEntry; state: ModelInstallState | undefined; unavailable: string | null; enabled: boolean }) {
  const engine = useEngine();
  const { durable: databaseDurable } = useEngineStatus();
  const download = useModelDownload();
  const size = megabytes(entry.downloadBytes);
  const run = async (action: Exclude<Action, 'download'>): Promise<void> => {
    try {
      if (action === 'pause') await engine.models.pauseDownload(entry.id);
      else if (action === 'cancel') {
        await engine.models.cancelDownload(entry.id);
        showToast('Download cancelled · partial files deleted');
      } else {
        await engine.models.removeDownload(entry.id);
        showToast(`${entry.name} removed from this device`);
      }
    } catch (error) {
      reportError(error);
    }
  };
  const confirmRemove = (): void => {
    openDialog('confirm', {
      title: 'Remove from this device?',
      message: `Delete the downloaded files of ${entry.name} (${size}) from this browser?`,
      note: enabled
        ? 'The model stays switched on: new messages wait for it until it is downloaded again. Its scores, rules and history stay.'
        : 'Its scores, rules and history stay. Download it again at any time.',
      confirmLabel: 'Remove',
      onConfirm: () => run('remove'),
    });
  };

  if (unavailable !== null && !state) {
    return (
      <div className="model-install" data-model-install={entry.id} data-phase="unavailable" aria-live="polite">
        <div className="model-install-row">
          <span className="model-install-status is-error">
            <Icon name="info" />
            <span>{unavailable}</span>
          </span>
        </div>
        <p className="model-preview-note">Scores of this model can still come from another device of yours that has it, through sync.</p>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="model-install" data-model-install={entry.id} data-phase="loading" aria-live="polite">
        <div className="model-install-row">
          <span className="model-install-status">
            <Icon name="loader" />
            <span>Checking this device…</span>
          </span>
        </div>
      </div>
    );
  }

  const pct = percent(state);
  const busy = state.phase === 'downloading' || state.phase === 'verifying' || state.phase === 'paused';
  const { memoryGb } = deviceHints();
  let icon = 'download';
  let text: string;
  let note: string | null = null;
  const buttons: JSX.Element[] = [];
  const button = (action: string, label: string, iconName: string, onClick: () => void, primary = false): JSX.Element => (
    <button key={action} type="button" className={primary ? 'primary-button' : 'secondary-button'} data-action={`model-${action}`} onClick={onClick}>
      <Icon name={iconName} />
      {label}
    </button>
  );
  switch (state.phase) {
    case 'absent':
      text = state.evicted ? 'The browser removed the downloaded model' : 'Not downloaded on this device';
      icon = state.evicted ? 'circle-alert' : 'download';
      note = state.evicted
        ? 'The browser removed the downloaded model. Download it again. Messages wait for it; nothing failed.'
        : `${size} from Digester’s own server, checked against pinned checksums. ${enabled ? 'It is switched on: messages wait for it until it is downloaded.' : 'Nothing is downloaded until you click Download.'}`;
      buttons.push(button('download', state.evicted ? `Download again (${size})` : `Download (${size})`, 'download', () => download(entry), true));
      break;
    case 'downloading':
      text = `Downloading · ${pct} %`;
      buttons.push(button('pause', 'Pause', 'pause', () => void run('pause')), button('cancel', 'Cancel', 'x', () => void run('cancel')));
      note = 'You can keep reading; the download continues in the background while a Digester tab is open.';
      break;
    case 'paused':
      text = `Paused at ${pct} %`;
      icon = 'pause';
      buttons.push(button('resume', 'Resume', 'play', () => download(entry), true), button('cancel', 'Cancel', 'x', () => void run('cancel')));
      note = 'The part already downloaded is kept; Resume continues from there.';
      break;
    case 'verifying':
      text = 'Verifying the downloaded files';
      icon = 'shield-check';
      note = 'Checking every file against its pinned SHA-256 before first use.';
      break;
    case 'installed':
      text = state.loaded ? 'Downloaded · running now' : 'Downloaded on this device';
      icon = 'check';
      buttons.push(button('remove', 'Remove from this device', 'trash', confirmRemove));
      note = enabled ? 'Scores new messages on this device: free, private, no tokens.' : 'Downloaded but switched off: switch it on to let it score.';
      break;
    case 'error':
      icon = 'circle-alert';
      note = installErrorText(state);
      if (cannotStart(state)) {
        // Installed files whose session cannot start (review M-R2): Retry tries the session again; the files stay.
        text = 'Could not start on this device';
        buttons.push(button('retry', 'Retry', 'rotate-ccw', () => download(entry), true), button('remove', 'Remove from this device', 'trash', confirmRemove));
      } else {
        text = 'Download failed';
        buttons.push(button('retry', 'Retry', 'rotate-ccw', () => download(entry), true), button('cancel', 'Cancel', 'x', () => void run('cancel')));
      }
      break;
    case 'unsupported':
      text = 'This browser cannot run on-device models';
      icon = 'info';
      note = 'Scores of this model can still come from another device of yours that has it, through sync.';
      break;
  }
  return (
    <div className="model-install" data-model-install={entry.id} data-phase={state.phase} aria-live="polite">
      <div className="model-install-row">
        <span className={state.phase === 'error' || state.evicted ? 'model-install-status is-error' : 'model-install-status'}>
          <Icon name={icon} />
          <span data-install-text>{text}</span>
        </span>
        <div className="model-install-actions">{buttons}</div>
      </div>
      {busy ? (
        <>
          <progress className="model-progress" value={state.phase === 'verifying' ? 100 : pct} max={100} aria-label="Download progress" />
          <div className="model-download-meta">
            <span>{state.phase === 'verifying' ? 'Checking files' : `${megabytes(state.bytesDone)} of ${megabytes(state.bytesTotal || entry.downloadBytes)}`}</span>
            <span>{state.phase === 'verifying' ? '100%' : `${pct}%`}</span>
          </div>
        </>
      ) : null}
      {note ? <p className="model-preview-note">{note}</p> : null}
      {state.phase === 'installed' || busy ? (
        <p className="model-preview-note" data-durable={state.durable ? 'yes' : 'no'}>
          {state.durable
            ? 'Stored persistently: the browser keeps the files until you remove them.'
            : databaseDurable === false
              ? 'This browser cannot keep the model; it would be downloaded again every time.'
              : 'Storage is not persistent here: the browser may remove the files when space runs low (you will be told).'}
        </p>
      ) : databaseDurable === false && state.phase === 'absent' ? (
        <p className="model-preview-note" data-durable="no">This browser cannot keep the model; it would be downloaded again every time.</p>
      ) : null}
      {memoryGb !== null && memoryGb < 4 && state.phase !== 'unsupported' ? (
        <p className="model-preview-note">This device reports {memoryGb} GB of memory; the model uses about {(entry.memoryMb / 1000).toFixed(1)} GB while it runs and may be slow here.</p>
      ) : null}
    </div>
  );
}
